import React, { useState } from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { ASSETS } from "@/lib/assets";
import { Skull, ArrowLeft } from "lucide-react";
import { toast } from "sonner";

export default function AuthPage() {
  const [mode, setMode] = useState("login");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [callsign, setCallsign] = useState("");
  const [rememberMe, setRememberMe] = useState(true);
  const { login, register, loading } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  const googleSignIn = () => {
    const redirectUrl = window.location.origin + "/command";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const submit = async (e) => {
    e.preventDefault();
    const fn = mode === "login"
      ? login(email, password, rememberMe)
      : register(email, password, callsign, rememberMe);
    const res = await fn;
    if (res.ok) {
      toast.success(mode === "login" ? "The dead remember you." : "You wake up. You are hungry.");
      const goTo = location.state?.goTo || "/command";
      navigate(goTo);
    } else {
      toast.error(res.error);
    }
  };

  return (
    <div className="relative min-h-screen overflow-hidden">
      <div className="absolute inset-0 z-0">
        <img src={ASSETS.hero} alt="" className="w-full h-full object-cover opacity-30" />
        <div className="absolute inset-0"
             style={{ background: "linear-gradient(90deg, rgba(3,3,3,0.95) 0%, rgba(3,3,3,0.6) 100%)" }} />
      </div>

      <div className="relative z-10 min-h-screen grid grid-cols-1 lg:grid-cols-12">
        {/* Left form panel */}
        <div className="lg:col-span-5 px-6 sm:px-12 py-10 lg:py-16 flex flex-col">
          <Link to="/" className="btn-ghost inline-flex items-center gap-2 self-start" data-testid="back-home-link">
            <ArrowLeft className="w-3 h-3" /> RETREAT
          </Link>

          <div className="flex-1 flex flex-col justify-center max-w-md mt-12">
            <div className="flex items-center gap-3 mb-10">
              <Skull className="w-5 h-5 text-blood" />
              <span className="tac-label">{mode === "login" ? "RETURN TO THE HORDE" : "FRESHLY DEAD"}</span>
            </div>

            <h1 className="font-serif-h text-5xl lg:text-6xl uppercase text-bone leading-none mb-8">
              {mode === "login" ? "Walk again." : "Rise."}
            </h1>

            <form onSubmit={submit} className="space-y-7">
              {mode === "register" && (
                <div>
                  <label className="tac-label">CALLSIGN</label>
                  <input
                    type="text"
                    value={callsign}
                    onChange={(e) => setCallsign(e.target.value)}
                    className="brutal-input"
                    placeholder="THE NAME THE LIVING WILL CURSE"
                    required
                    minLength={2}
                    maxLength={32}
                    data-testid="auth-callsign-input"
                  />
                </div>
              )}
              <div>
                <label className="tac-label">EMAIL</label>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="brutal-input"
                  placeholder="YOU@CARRION.NET"
                  required
                  data-testid="auth-email-input"
                />
              </div>
              <div>
                <label className="tac-label">PASSWORD</label>
                <input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="brutal-input"
                  placeholder="********"
                  required
                  minLength={6}
                  data-testid="auth-password-input"
                />
              </div>

              <button type="submit" className="btn-blood w-full mt-4" disabled={loading} data-testid="auth-submit-button">
                {loading ? "..." : mode === "login" ? "WALK AGAIN" : "RISE FROM THE DIRT"}
              </button>

              <div className="flex items-center gap-4 mt-2">
                <div className="flex-1 h-px bg-[var(--blood)]/30" />
                <span className="font-mono text-[0.6rem] tracking-[0.25em] text-muted-h/60">OR</span>
                <div className="flex-1 h-px bg-[var(--blood)]/30" />
              </div>

              <button
                type="button"
                onClick={googleSignIn}
                className="btn-ghost w-full flex items-center justify-center gap-3 !py-3 border border-[var(--blood)]/40 hover:border-[var(--blood)]"
                data-testid="google-signin-button"
              >
                <svg className="w-4 h-4" viewBox="0 0 24 24" aria-hidden="true">
                  <path fill="#EA4335" d="M12 5.04c1.62 0 3.06.56 4.2 1.64l3.12-3.12C17.46 1.8 14.96.75 12 .75 7.42.75 3.48 3.38 1.56 7.22l3.66 2.84C6.14 7.24 8.84 5.04 12 5.04z"/>
                  <path fill="#4285F4" d="M23.25 12.27c0-.93-.08-1.6-.26-2.3H12v4.35h6.44c-.13 1.08-.83 2.7-2.39 3.79l3.57 2.77c2.14-1.97 3.63-4.88 3.63-8.61z"/>
                  <path fill="#FBBC05" d="M5.22 14.29a6.98 6.98 0 0 1-.38-2.29c0-.8.14-1.57.37-2.29L1.56 6.87A11.2 11.2 0 0 0 .75 12c0 1.81.44 3.52 1.21 5.05l3.26-2.76z"/>
                  <path fill="#34A853" d="M12 23.25c3.04 0 5.6-1 7.46-2.72l-3.57-2.77c-.95.66-2.23 1.13-3.89 1.13-3.16 0-5.86-2.2-6.79-5.13l-3.65 2.83c1.91 3.85 5.86 6.66 10.44 6.66z"/>
                </svg>
                SIGN IN WITH GOOGLE
              </button>

              <label className="flex items-center gap-3 mt-4 cursor-pointer select-none group"
                     data-testid="remember-me-label">
                <input
                  type="checkbox"
                  checked={rememberMe}
                  onChange={(e) => setRememberMe(e.target.checked)}
                  className="w-4 h-4 accent-[var(--blood)] cursor-pointer"
                  data-testid="remember-me-checkbox"
                />
                <span className="font-mono text-[0.7rem] tracking-[0.2em] text-muted-h group-hover:text-bone transition-colors">
                  REMEMBER ME — STAY DEAD FOR 30 DAYS
                </span>
              </label>
              <p className="text-[0.65rem] text-muted-h/60 mt-1 leading-relaxed">
                Off → log out when you close the browser. On → stay logged in for a month.
              </p>
            </form>

            <button
              onClick={() => setMode(mode === "login" ? "register" : "login")}
              className="btn-ghost mt-8 self-start"
              data-testid="auth-toggle-mode"
            >
              {mode === "login" ? "FRESHLY DEAD? JOIN THE HORDE →" : "ALREADY RISEN? RETURN →"}
            </button>
          </div>
        </div>

        {/* Right pillar */}
        <div className="hidden lg:flex lg:col-span-7 items-end justify-end px-12 py-16">
          <div className="max-w-md text-right">
            <p className="tac-label mb-4">THE LAWS</p>
            <h2 className="font-serif-h text-4xl uppercase text-bone mb-6 leading-tight">
              You bring no weapons.<br />
              The humans do.<br />
              <span className="text-blood">Your dead never wake up twice.</span>
            </h2>
          </div>
        </div>
      </div>
    </div>
  );
}
